import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { TaskCompletion } from '../models';
import { TaskListItem, TaskListDetail } from './TaskListItem';

export enum HourlyViewMode {
  Default = 'default',
  Collapsed = 'collapsed',
  Expanded = 'expanded',
}

export interface LastFourHoursTasks {
  [key: string]: {
    completed: number;
    total: number;
    summary: string;
  };
}

interface HourlyChecklistProps {
  hourlyTasks: TaskCompletion[];
  lastFourHoursTasks: LastFourHoursTasks;
  viewMode: HourlyViewMode;
  viewModeChange: any;
  filterComplete: any;
  toggleTaskComplete: any;
}

const HourlyChecklist = ({
  hourlyTasks,
  lastFourHoursTasks,
  viewMode,
  viewModeChange,
  filterComplete,
  toggleTaskComplete,
}: HourlyChecklistProps) => {
  const intl = useIntl();
  const [hourlyViewMode, setHourlyViewMode] = useState<HourlyViewMode>(viewMode);
  const [completed, setCompleted] = useState(filterComplete(hourlyTasks).length);
  const [completedSummary, setCompletedSummary] = useState(`${completed}/${hourlyTasks.length}`);

  const formatHour = (hour: string) => {
    const hourNumber = parseInt(hour);
    const period = hourNumber >= 12 ? 'pm' : 'am';
    const displayHour = hourNumber === 0 ? 12 : hourNumber > 12 ? hourNumber - 12 : hourNumber;
    return `${displayHour} ${period}`;
  };

  const handleHourlyLeftButton = () => {
    if (hourlyViewMode == HourlyViewMode.Default) {
      setHourlyViewMode(HourlyViewMode.Expanded);
    } else if (hourlyViewMode == HourlyViewMode.Collapsed) {
      setHourlyViewMode(HourlyViewMode.Default);
    } else if (hourlyViewMode == HourlyViewMode.Expanded) {
      setHourlyViewMode(HourlyViewMode.Default);
    }
  };

  const handleHourlyRightButton = () => {
    setHourlyViewMode(HourlyViewMode.Collapsed);
  };

  const formatLastFourHoursTasks = () => {
    if (!lastFourHoursTasks) {
      return [];
    }
    const keys = Object.keys(lastFourHoursTasks);
    return keys.sort().map((key: string) => {
      const task = lastFourHoursTasks[key as keyof LastFourHoursTasks];
      return { hour: key, completed: task.completed, total: task.total, summary: task.summary };
    });
  };

  // Keyboard shortcut for toggling hourly view mode
  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.ctrlKey && event.key === 'h') {
      event.preventDefault();
      if (hourlyViewMode === HourlyViewMode.Collapsed) {
        setHourlyViewMode(HourlyViewMode.Default);
      } else if (hourlyViewMode === HourlyViewMode.Default) {
        setHourlyViewMode(HourlyViewMode.Expanded);
      } else {
        setHourlyViewMode(HourlyViewMode.Collapsed);
      }
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [hourlyViewMode]);

  useEffect(() => {
    if (viewModeChange) viewModeChange(hourlyViewMode);
  }, [hourlyViewMode]);

  useEffect(() => {
    setCompleted(filterComplete(hourlyTasks).length);
  }, [hourlyTasks]);

  useEffect(() => {
    setCompletedSummary(`${completed}/${hourlyTasks.length}`);
  }, [completed, hourlyTasks.length]);

  return (
    <div className="tasks-hourly">
      <div className="hourly-toggle-container">
        <button
          className="button hourly-toggle-expand"
          onClick={handleHourlyLeftButton}
          title={'Toggle hourly checklist'}
        >
          <i className="uil uil-angle-left"></i>
        </button>

        <div className="hourly-header-title">
          {intl.formatMessage({ id: 'walkthroughChecklist' })}
        </div>
        <span>
          {hourlyViewMode !== HourlyViewMode.Collapsed && (
            <button
              className="button hourly-toggle-collapse"
              onClick={handleHourlyRightButton}
              title={'Collapse hourly checklist'}
            >
              <i className="uil uil-times"></i>
            </button>
          )}
        </span>
      </div>
      <div className="hour-completion-list">
        {formatLastFourHoursTasks().map((stat, index) => {
          const isCurrentHour = index === 3; // Fourth hour is current
          const isOverdue =
            stat.total > 0 &&
            stat.completed < stat.total &&
            new Date().getHours() > parseInt(stat.hour);

          return (
            <div
              key={stat.hour}
              style={{
                border: isCurrentHour
                  ? '2px solid #007bff'
                  : isOverdue
                  ? '2px solid #dc3545'
                  : '1px solid #dee2e6',
                backgroundColor: isCurrentHour ? '#f8f9ff' : isOverdue ? '#fff5f5' : 'white',
              }}
            >
              <span>{formatHour(stat.hour)}</span>
              <div>
                <div>{isCurrentHour ? completedSummary : stat.summary}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="hourly-list">
        {/* Incomplete hourly tasks */}
        {filterComplete(hourlyTasks, true)
          .sort((a: any, b: any) => {
            return a.task.name.localeCompare(b.task.name);
          })
          .filter((task: TaskCompletion) => task.task?.interval === 'Hourly')
          .map((task: TaskCompletion, i: number) => (
            <TaskListItem
              taskCompletion={task}
              detailType={TaskListDetail.Subtitle}
              displayOptionsMenu={false}
              displayTimeInfo={false}
              key={`incomplete-hourly-${i}`}
              toggleCompletion={toggleTaskComplete}
            />
          ))}

        {/* Completed hourly tasks section */}
        {filterComplete(hourlyTasks).length > 0 && (
          <>
            <div className="completed-section-divider">
              <span>
                {intl.formatMessage({ id: 'completed' })} ({filterComplete(hourlyTasks).length})
              </span>
            </div>
            {filterComplete(hourlyTasks).map((task: TaskCompletion, i: number) => (
              <TaskListItem
                taskCompletion={task}
                detailType={TaskListDetail.Subtitle}
                displayOptionsMenu={false}
                displayTimeInfo={false}
                key={`completed-hourly-${i}`}
                toggleCompletion={toggleTaskComplete}
              />
            ))}
          </>
        )}
        {hourlyTasks.length == 0 && (
          <p className="tasks-not-found center">
            {intl.formatMessage({ id: 'hourlyTasksMissing' })}
          </p>
        )}
      </div>
    </div>
  );
};

export default HourlyChecklist;
